import React, { useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../DashboardLayout';
import { Button } from '../../ui';
import { UploadCloudIcon, FileSpreadsheetIcon, CheckCircle2Icon, SparklesIcon, ArrowRightIcon, ArrowLeftIcon, XIcon } from 'lucide-react';
type Step = 'upload' | 'mapping' | 'analyzing' | 'done';
export function ClientUploadPage() {
  const [step, setStep] = useState<Step>('upload');
  const [fileName, setFileName] = useState<string>('');
  const navigate = useNavigate();
  const handleFile = (name: string) => {
    setFileName(name);
    setStep('mapping');
  };
  const runAnalysis = () => {
    setStep('analyzing');
    setTimeout(() => setStep('done'), 2600);
  };
  return <DashboardLayout pageTitle="Import Client Book" breadcrumb="Revenue Intelligence / Import" data-id="element-947">
      {/* Stepper */}
      <div className="flex items-center justify-center gap-2 mb-10" data-id="element-948">
        {(['upload', 'mapping', 'analyzing', 'done'] as Step[]).map((s, i) => {
        const order = ['upload', 'mapping', 'analyzing', 'done'];
        const active = order.indexOf(step) >= i;
        return <Fragment key={s} data-id="element-949">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${active ? 'bg-brand-navy text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-950">
                <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px]" data-id="element-951">
                  {i + 1}
                </span>
                {s === 'upload' && 'Upload'}
                {s === 'mapping' && 'Map fields'}
                {s === 'analyzing' && 'AI analysis'}
                {s === 'done' && 'Review'}
              </div>
              {i < 3 && <div className={`h-px w-8 ${active ? 'bg-brand-navy' : 'bg-slate-200'}`} data-id="element-952" />}
            </Fragment>;
      })}
      </div>

      <div className="max-w-3xl mx-auto" data-id="element-953">
        <AnimatePresence mode="wait" data-id="element-954">
          {step === 'upload' && <motion.div key="upload" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} data-id="element-955">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8" data-id="element-956">
                <h2 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-957">
                  Upload your client book
                </h2>
                <p className="text-slate-600 mb-8" data-id="element-958">
                  Drop a CSV or Excel export from your CRM, PMS, or sales tool.
                  We'll detect contacts, bookings, and revenue automatically.
                </p>

                <button onClick={() => handleFile('client_book_q4.csv')} className="w-full border-2 border-dashed border-slate-300 hover:border-brand-teal hover:bg-brand-teal/5 rounded-2xl p-12 flex flex-col items-center justify-center text-center transition-colors group" data-id="element-959">
                  <div className="w-14 h-14 rounded-full bg-brand-teal/10 text-brand-teal flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-960">
                    <UploadCloudIcon className="w-7 h-7" data-id="element-961" />
                  </div>
                  <div className="font-bold text-brand-navy mb-1" data-id="element-962">
                    Drop CSV/XLSX or click to upload
                  </div>
                  <div className="text-sm text-slate-500" data-id="element-963">
                    Supports HubSpot, Salesforce, Opera, Delphi, or generic CSV
                  </div>
                </button>

                <div className="grid sm:grid-cols-3 gap-3 mt-6" data-id="element-964">
                  {[{
                name: 'HubSpot',
                icon: '🟧'
              }, {
                name: 'Salesforce',
                icon: '☁️'
              }, {
                name: 'Opera PMS',
                icon: '🏨'
              }].map(src => <button key={src.name} className="border border-slate-200 rounded-xl p-4 text-left hover:border-brand-teal hover:bg-brand-teal/5 transition-colors" data-id="element-965">
                      <div className="text-lg mb-1" data-id="element-966">{src.icon}</div>
                      <div className="text-sm font-bold text-brand-navy" data-id="element-967">
                        Connect {src.name}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-968">Direct sync</div>
                    </button>)}
                </div>
              </div>
            </motion.div>}

          {step === 'mapping' && <motion.div key="mapping" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} data-id="element-969">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8" data-id="element-970">
                <div className="flex items-center justify-between mb-6" data-id="element-971">
                  <div data-id="element-972">
                    <h2 className="text-2xl font-bold text-brand-navy mb-1" data-id="element-973">
                      Map your columns
                    </h2>
                    <p className="text-slate-600 text-sm" data-id="element-974">
                      Detected 247 rows in{' '}
                      <span className="font-bold" data-id="element-975">{fileName}</span>
                    </p>
                  </div>
                  <button onClick={() => setStep('upload')} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg" data-id="element-976">
                    <XIcon className="w-5 h-5" data-id="element-977" />
                  </button>
                </div>

                <div className="space-y-3" data-id="element-978">
                  {[{
                ours: 'Company name',
                theirs: 'company_name',
                auto: true
              }, {
                ours: 'Contact name',
                theirs: 'primary_contact',
                auto: true
              }, {
                ours: 'Industry',
                theirs: 'industry_segment',
                auto: true
              }, {
                ours: 'Total revenue',
                theirs: 'lifetime_spend_usd',
                auto: true
              }, {
                ours: 'Last booking date',
                theirs: 'last_event_date',
                auto: true
              }, {
                ours: 'Number of bookings',
                theirs: 'event_count',
                auto: true
              }, {
                ours: 'Notes / signals',
                theirs: 'sales_notes',
                auto: false
              }].map(row => <div key={row.ours} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-4 p-3 bg-slate-50 rounded-lg" data-id="element-979">
                      <div className="text-sm font-bold text-brand-navy" data-id="element-980">
                        {row.ours}
                      </div>
                      <ArrowRightIcon className="w-4 h-4 text-slate-400" data-id="element-981" />
                      <div className="text-sm text-slate-700 font-mono bg-white border border-slate-200 px-3 py-1.5 rounded" data-id="element-982">
                        {row.theirs}
                      </div>
                      {row.auto ? <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full flex items-center gap-1" data-id="element-983">
                          <CheckCircle2Icon className="w-3 h-3" data-id="element-984" /> Auto
                        </span> : <span className="text-xs font-bold text-amber-700 bg-amber-50 px-2 py-1 rounded-full" data-id="element-985">
                          Review
                        </span>}
                    </div>)}
                </div>

                <div className="flex justify-between mt-8 pt-6 border-t border-slate-100" data-id="element-986">
                  <Button variant="outline" onClick={() => setStep('upload')} data-id="element-987">
                    <ArrowLeftIcon className="w-4 h-4 mr-2" data-id="element-988" /> Back
                  </Button>
                  <Button variant="primary" onClick={runAnalysis} data-id="element-989">
                    <SparklesIcon className="w-4 h-4 mr-2" data-id="element-990" />
                    Run AI analysis
                  </Button>
                </div>
              </div>
            </motion.div>}

          {step === 'analyzing' && <motion.div key="analyzing" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="bg-brand-navy rounded-2xl shadow-soft p-12 text-white text-center relative overflow-hidden" data-id="element-991">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-teal/10 via-transparent to-brand-coral/10" data-id="element-992" />
              <div className="relative z-10" data-id="element-993">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-16 h-16 mx-auto mb-6 rounded-full border-2 border-brand-teal/30 border-t-brand-teal" data-id="element-994" />
                <h2 className="text-2xl font-bold mb-2" data-id="element-995">
                  Analyzing 247 clients…
                </h2>
                <p className="text-slate-300 mb-8 max-w-md mx-auto" data-id="element-996">
                  Scoring upsell probability, projecting LTV, and detecting
                  rebooking signals across your entire book.
                </p>
                <div className="space-y-2 max-w-md mx-auto text-left" data-id="element-997">
                  {['Parsing booking history', 'Detecting recurring patterns', 'Scoring upsell probability', 'Projecting lifetime value'].map((s, i) => <motion.div key={s} initial={{
                opacity: 0,
                x: -10
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: i * 0.4
              }} className="flex items-center gap-2 text-sm text-slate-300" data-id="element-998">
                      <CheckCircle2Icon className="w-4 h-4 text-brand-teal" data-id="element-999" />
                      {s}
                    </motion.div>)}
                </div>
              </div>
            </motion.div>}

          {step === 'done' && <motion.div key="done" initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} data-id="element-1000">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-soft p-8 text-center" data-id="element-1001">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center" data-id="element-1002">
                  <CheckCircle2Icon className="w-8 h-8" data-id="element-1003" />
                </div>
                <h2 className="text-2xl font-bold text-brand-navy mb-2" data-id="element-1004">
                  Analysis complete
                </h2>
                <p className="text-slate-600 mb-8" data-id="element-1005">
                  We identified{' '}
                  <span className="font-bold text-brand-coral" data-id="element-1006">
                    23 hot upsell opportunities
                  </span>{' '}
                  worth an estimated{' '}
                  <span className="font-bold text-brand-navy" data-id="element-1007">$612K</span> in
                  incremental revenue.
                </p>

                <div className="grid grid-cols-3 gap-4 mb-8 text-left" data-id="element-1008">
                  <div className="bg-slate-50 rounded-xl p-4" data-id="element-1009">
                    <div className="text-xs text-slate-500 uppercase font-bold mb-1" data-id="element-1010">
                      Imported
                    </div>
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-1011">
                      247
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1012">clients</div>
                  </div>
                  <div className="bg-brand-coral/5 rounded-xl p-4" data-id="element-1013">
                    <div className="text-xs text-brand-coral uppercase font-bold mb-1" data-id="element-1014">
                      Hot
                    </div>
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-1015">23</div>
                    <div className="text-xs text-slate-500" data-id="element-1016">
                      ready-to-upsell
                    </div>
                  </div>
                  <div className="bg-brand-teal/5 rounded-xl p-4" data-id="element-1017">
                    <div className="text-xs text-brand-teal uppercase font-bold mb-1" data-id="element-1018">
                      LTV
                    </div>
                    <div className="text-2xl font-bold text-brand-navy" data-id="element-1019">
                      $4.2M
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-1020">
                      projected total
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-3" data-id="element-1021">
                  <Link to="/dashboard/clients" data-id="element-1022">
                    <Button variant="primary" data-id="element-1023">
                      Open Client Book{' '}
                      <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1024" />
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </DashboardLayout>;
}